import { ValueError } from "../error/ValueError.js";
import { Feedback } from "../feedback/Feedback.js";
import { updateData } from "../util/update.js";
import { validateWithContext } from "../util/validate.js";
import { AsyncThroughProvider, ThroughProvider } from "./ThroughProvider.js";
// Constants.
const VALIDATION_CONTEXT_GET = { action: "get", id: true };
const VALIDATION_CONTEXT_ADD = { action: "add" };
const VALIDATION_CONTEXT_SET = { action: "set" };
const VALIDATION_CONTEXT_UPDATE = { action: "update", partial: true };
/** Validate a synchronous source provider (source can have any type because validation guarantees the type). */
export class ValidationProvider extends ThroughProvider {
    schemas;
    constructor(schemas, source) {
        super(source);
        this.schemas = schemas;
    }
    /** Get a named schema. */
    getSchema(collection) {
        return this.schemas[collection];
    }
    getItem(collection, id) {
        return _validateItem(collection, super.getItem(collection, id), this.getSchema(collection));
    }
    async *getItemSequence(collection, id) {
        const schema = this.getSchema(collection);
        for await (const unsafeItem of super.getItemSequence(collection, id))
            yield _validateItem(collection, unsafeItem, schema);
    }
    addItem(collection, data) {
        return super.addItem(collection, validateWithContext(data, this.getSchema(collection), VALIDATION_CONTEXT_ADD));
    }
    setItem(collection, id, data) {
        return super.setItem(collection, id, validateWithContext(data, this.getSchema(collection), VALIDATION_CONTEXT_SET));
    }
    updateItem(collection, id, updates) {
        validateWithContext(updateData({}, updates), this.getSchema(collection), VALIDATION_CONTEXT_UPDATE);
        return super.updateItem(collection, id, updates);
    }
    deleteItem(collection, id) {
        return super.deleteItem(collection, id);
    }
    countQuery(collection, query) {
        return super.countQuery(collection, query);
    }
    getQuery(collection, query) {
        return _validateItems(collection, super.getQuery(collection, query), this.getSchema(collection));
    }
    setQuery(collection, query, data) {
        return super.setQuery(collection, query, this.getSchema(collection).validate(data));
    }
    updateQuery(collection, query, updates) {
        validateWithContext(updateData({}, updates), this.getSchema(collection), VALIDATION_CONTEXT_UPDATE);
        return super.updateQuery(collection, query, updates);
    }
    deleteQuery(collection, query) {
        return super.deleteQuery(collection, query);
    }
    async *getQuerySequence(collection, query) {
        const schema = this.getSchema(collection);
        for await (const unsafeItems of super.getQuerySequence(collection, query))
            yield _validateItems(collection, unsafeItems, schema);
    }
}
/** Validate an asynchronous source provider (source can have any type because validation guarantees the type). */
export class AsyncValidationProvider extends AsyncThroughProvider {
    schemas;
    constructor(schemas, source) {
        super(source);
        this.schemas = schemas;
    }
    getSchema(collection) {
        return this.schemas[collection];
    }
    async getItem(collection, id) {
        return _validateItem(collection, await super.getItem(collection, id), this.getSchema(collection));
    }
    async *getItemSequence(collection, id) {
        const schema = this.getSchema(collection);
        for await (const unsafeItem of super.getItemSequence(collection, id))
            yield _validateItem(collection, unsafeItem, schema);
    }
    addItem(collection, data) {
        return super.addItem(collection, validateWithContext(data, this.getSchema(collection), VALIDATION_CONTEXT_ADD));
    }
    setItem(collection, id, data) {
        return super.setItem(collection, id, validateWithContext(data, this.getSchema(collection), VALIDATION_CONTEXT_SET));
    }
    updateItem(collection, id, updates) {
        validateWithContext(updateData({}, updates), this.getSchema(collection), VALIDATION_CONTEXT_UPDATE);
        return super.updateItem(collection, id, updates);
    }
    deleteItem(collection, id) {
        return super.deleteItem(collection, id);
    }
    countQuery(collection, query) {
        return super.countQuery(collection, query);
    }
    async getQuery(collection, query) {
        return _validateItems(collection, await super.getQuery(collection, query), this.getSchema(collection));
    }
    async *getQuerySequence(collection, query) {
        const schema = this.getSchema(collection);
        for await (const unsafeItems of super.getQuerySequence(collection, query))
            yield _validateItems(collection, unsafeItems, schema);
    }
    setQuery(collection, query, data) {
        return super.setQuery(collection, query, validateWithContext(data, this.getSchema(collection), VALIDATION_CONTEXT_SET));
    }
    updateQuery(collection, query, updates) {
        validateWithContext(updateData({}, updates), this.getSchema(collection), VALIDATION_CONTEXT_UPDATE);
        return super.updateQuery(collection, query, updates);
    }
    deleteQuery(collection, query) {
        return super.deleteQuery(collection, query);
    }
}
function _validateItem(collection, unsafeEntity, schema) {
    if (!unsafeEntity)
        return undefined;
    try {
        return validateWithContext(unsafeEntity, schema, VALIDATION_CONTEXT_GET);
    }
    catch (thrown) {
        if (!(thrown instanceof Feedback))
            throw thrown;
        throw new ValueError(`Invalid data for "${collection}"`, thrown.message);
    }
}
/** Validate a set of entities for this query reference. */
function _validateItems(collection, unsafeEntities, schema) {
    return Array.from(_yieldValidItems(collection, unsafeEntities, schema));
}
function* _yieldValidItems(collection, unsafeEntities, schema) {
    let invalid = false;
    const messages = {};
    for (const unsafeEntity of unsafeEntities) {
        try {
            yield validateWithContext(unsafeEntity, schema, VALIDATION_CONTEXT_GET);
        }
        catch (thrown) {
            if (!(thrown instanceof Feedback))
                throw thrown;
            invalid = true;
            messages[unsafeEntity.id] = thrown.message;
        }
    }
    if (invalid)
        throw new ValueError(`Invalid data for "${collection}"`, messages);
}
